import {
  postRostersUnofficialUploadMutation,
  SideType,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  Icons,
  Input,
  Select,
  toast,
} from "@win-reality/react-ui";
import { Trash2 } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { flushSync } from "react-dom";

type PlayerRow = {
  rowId: string;
  firstName: string;
  lastName: string;
  jerseyNumber: string;
  throws: SideType | "";
  bats: SideType | "";
};

type CreatePlayersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  onPlayersCreated: (
    externalIds: string[],
    players: Array<Record<string, unknown>>,
  ) => void | Promise<void>;
  // Pass `null` to suppress the built-in success toast (e.g. when the
  // caller shows its own toast after a follow-up action).
  successToastMessage?: string | null;
  /** Dialog theme (TaggingV2 follows its light/dark toggle) */
  variant?: "light" | "dark";
};

const throwsOptions = [
  { label: "L", value: "left" as SideType },
  { label: "R", value: "right" as SideType },
];

const batsOptions = [
  { label: "L", value: "left" as SideType },
  { label: "R", value: "right" as SideType },
  { label: "S", value: "switch" as SideType },
];

const makeEmptyRow = (): PlayerRow => ({
  rowId: crypto.randomUUID(),
  firstName: "",
  lastName: "",
  jerseyNumber: "",
  throws: "",
  bats: "",
});

const GRID = "grid-cols-[2fr_2fr_3rem_3rem_3rem_1.5rem]";

type PlayerRowItemProps = {
  row: PlayerRow;
  onUpdate: (
    rowId: string,
    field: keyof Omit<PlayerRow, "rowId">,
    value: string,
  ) => void;
  onDelete: (rowId: string) => void;
  isFirst: boolean;
};

const PlayerRowItem = memo(function PlayerRowItem({
  row,
  onUpdate,
  onDelete,
  isFirst,
}: PlayerRowItemProps) {
  return (
    <div className={`grid ${GRID} gap-2 items-center`}>
      <Input
        value={row.firstName}
        onChange={(e) => onUpdate(row.rowId, "firstName", e.target.value)}
        placeholder=""
        className="bg-white dark:bg-input"
      />
      <Input
        value={row.lastName}
        onChange={(e) => onUpdate(row.rowId, "lastName", e.target.value)}
        placeholder=""
        className="bg-white dark:bg-input"
      />
      <Input
        value={row.jerseyNumber}
        onChange={(e) => onUpdate(row.rowId, "jerseyNumber", e.target.value)}
        placeholder=""
        className="bg-white dark:bg-input"
        type="number"
      />
      <div className="w-full">
        <Select
          value={row.throws}
          onValueChange={(val) => onUpdate(row.rowId, "throws", val)}
          items={throwsOptions}
          placeholder="-"
        />
      </div>
      <div className="w-full">
        <Select
          value={row.bats}
          onValueChange={(val) => onUpdate(row.rowId, "bats", val)}
          items={batsOptions}
          placeholder="-"
        />
      </div>
      {!isFirst && (
        <button
          onClick={() => onDelete(row.rowId)}
          className="text-gray-400 hover:text-red-500 transition-colors p-0"
          aria-label="Delete row"
        >
          <Trash2 className="size-4" />
        </button>
      )}
    </div>
  );
});

export function CreatePlayersModal({
  open,
  onOpenChange,
  orgId,
  onPlayersCreated,
  successToastMessage,
  variant,
}: CreatePlayersModalProps) {
  const [rows, setRows] = useState<PlayerRow[]>([makeEmptyRow()]);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);

  const { mutate: uploadPlayers, isPending } = useMutation(
    postRostersUnofficialUploadMutation(),
  );

  const isBusy = isPending || isProcessingCallback;

  const updateRow = useCallback(
    (rowId: string, field: keyof Omit<PlayerRow, "rowId">, value: string) => {
      setRows((prev) =>
        prev.map((r) => (r.rowId === rowId ? { ...r, [field]: value } : r)),
      );
    },
    [],
  );

  const addRow = () => setRows((prev) => [...prev, makeEmptyRow()]);

  const deleteRow = useCallback((rowId: string) => {
    setRows((prev) => prev.filter((r) => r.rowId !== rowId));
  }, []);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen && isBusy) return;
    if (!nextOpen) setRows([makeEmptyRow()]);
    onOpenChange(nextOpen);
  };

  const handleSave = () => {
    const validRows = rows.filter(
      (r) => r.firstName.trim() && r.lastName.trim() && r.throws && r.bats,
    );

    if (validRows.length === 0) {
      toast(
        "Please complete at least one player row (first name, last name, throws, bats).",
        {
          variant: "error",
        },
      );
      return;
    }

    const csvLines = [
      "last,first,id,bats,throws,number",
      ...validRows.map(
        (r) =>
          `${r.lastName},${r.firstName},,${r.bats},${r.throws},${r.jerseyNumber}`,
      ),
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv" });

    uploadPlayers(
      {
        body: { file: [blob] as unknown as File[] },
        query: { orgId, teamName: undefined },
      },
      {
        onSuccess: async (data) => {
          const members = data?.team?.unofficial_members ?? [];
          const externalIds = members
            .map((m) => {
              const member = m as unknown as Record<string, unknown>;
              return (member.external_id ?? member.externalId) as
                | string
                | undefined;
            })
            .filter((id): id is string => Boolean(id));
          setIsProcessingCallback(true);
          try {
            await Promise.resolve(onPlayersCreated(externalIds, members));
          } finally {
            setIsProcessingCallback(false);
          }
          const message =
            successToastMessage === undefined
              ? "Players created successfully!"
              : successToastMessage;
          flushSync(() => {
            setRows([makeEmptyRow()]);
            onOpenChange(false);
          });
          if (message) {
            toast(message, { variant: "success" });
          }
        },
        onError: (error) => {
          try {
            const detail = JSON.parse((error as Error).message)?.detail;
            toast(detail ?? "Failed to create players", { variant: "error" });
          } catch {
            toast("Failed to create players", { variant: "error" });
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
      size="lg"
      variant={variant}
      title="Create Players"
      primaryAction={{
        label: "Save Players",
        onClick: handleSave,
        isDisabled: isBusy,
        isLoading: isBusy,
      }}
    >
      <div className="space-y-3">
        <div className={`grid ${GRID} gap-2 items-center px-1`}>
          <div className="text-xs font-light uppercase text-gray-400">
            First Name
          </div>
          <div className="text-xs font-light uppercase text-gray-400">
            Last Name
          </div>
          <div className="text-xs font-light uppercase text-gray-400">
            Jersey #
          </div>
          <div className="text-xs font-light uppercase text-gray-400">
            Throws
          </div>
          <div className="text-xs font-light uppercase text-gray-400">Bats</div>
          <div />
        </div>

        <div className="max-h-[400px] overflow-y-auto space-y-2 p-1">
          {rows.map((row, index) => (
            <PlayerRowItem
              key={row.rowId}
              row={row}
              onUpdate={updateRow}
              onDelete={deleteRow}
              isFirst={index === 0}
            />
          ))}
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="text-blue relative -left-4"
          onClick={addRow}
        >
          <Icons.Plus className="size-4" />
          Add New Player
        </Button>
      </div>
    </Dialog>
  );
}
